/**
 * Service for unit correction analysis
 * Handles detection and correction of unit mismatches in lab results
 */

import { apiService } from './apiService';

export interface UnitCorrectionResult {
  correctedValue: number;
  correctedUnit: string;
  correctionMethod: 'none' | 'multiplication' | 'division' | 'conversion';
  confidence: 'high' | 'medium' | 'low';
  explanation?: string;
}

export class UnitCorrectionService {
  /**
   * Analyze and potentially correct a unit mismatch
   */
  static async analyzeAndCorrectUnit(
    testName: string,
    value: number,
    unit: string,
    referenceRange: { min: number | null; max: number | null },
    sessionId?: string | null
  ): Promise<UnitCorrectionResult> {
    try {
      const response = await apiService.post('/api/analyze-unit', {
        sessionId,
        testName,
        value,
        units: unit,  // Backend expects 'units' not 'unit'
        referenceRange: {
          min: referenceRange.min,
          max: referenceRange.max
        }
      });

      if (response.status === 'success' && response.data) {
        // Backend returns the correction data directly in response.data
        return {
          correctedValue: response.data.correctedValue,
          correctedUnit: response.data.correctedUnit,
          correctionMethod: response.data.correctionMethod,
          confidence: response.data.confidence as 'high' | 'medium' | 'low'
        };
      } else {
        // Return no correction on error
        return {
          correctedValue: value,
          correctedUnit: unit,
          correctionMethod: 'none',
          confidence: 'low'
        };
      }
    } catch (error) {
      console.error('Error analyzing unit:', error);
      // Return no correction on error
      return {
        correctedValue: value,
        correctedUnit: unit,
        correctionMethod: 'none',
        confidence: 'low'
      };
    }
  }
}